import { inject, Injectable } from '@angular/core';
import { CreatorStateService } from './CreatorStateService';
import { forkJoin, mergeMap, Observable, of, Subject, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ImageElementsApiService, SectorsApiService, SectorsConfigurationsApiService, ShapesApiService } from '../api-services';
import { ISeatsPlan } from '../../interfaces';
import { CreatorCacheService } from './CreatorCacheService';
import { DictionariesService } from 'src/app/core/services/DictionariesService';
import { ZonesService } from 'src/app/prices/core/services/ZonesService';

@Injectable({
  providedIn: 'root'
})
export class AuditoriumCreatorService {

  private readonly _sectorsApiService = inject(SectorsApiService);
  private readonly _dictionariesService = inject(DictionariesService);
  private readonly _shapesApiService = inject(ShapesApiService);
  private readonly _imageElementsApiService = inject(ImageElementsApiService);
  private readonly _sectorsConfigurationsApiService = inject(SectorsConfigurationsApiService);
  private readonly _zonesService = inject(ZonesService);

  public init(id: string): Observable<ISeatsPlan> {
    CreatorStateService.sectorId = +id;

    return forkJoin([
      this._sectorsApiService.getOneSector(CreatorStateService.sectorId).pipe(
        mergeMap(() => forkJoin([
          this.getCreatorDataFromApi('shapes'),
          this.getCreatorDataFromApi('images'),
        ]))
      ),
      this._dictionariesService.getSeatTypes().pipe(tap((res) => CreatorStateService.seatTypes = res.items)),
      this._dictionariesService.getDefaultSeatStatuses().pipe(tap((res) => CreatorStateService.seatStatuses = res.items))
    ]).pipe(switchMap(() => of(CreatorStateService.seatsPlan)));
  }

  public getCreatorDataFromApi(dataType: 'shapes' | 'images'): Observable<boolean> {
    return this.getDataObservableWrapper((next: (value: boolean) => void, error: (err: string) => void) => {
      switch (dataType) {
        case 'shapes':
          this.getShapes().subscribe(() => next(true));
          break;
        case 'images':
          this.getImages().subscribe(() => next(true));
          break;
      }
    });
  }

  public loadCreatorZones(): Observable<ISeatsPlan> {
    return this._sectorsConfigurationsApiService.getAllSectorsConfigurations(CreatorStateService.sectorId).pipe(
      switchMap(() => forkJoin([
        this._sectorsApiService.getOneSector(
          CreatorStateService.sectorId,
          CreatorStateService.configurations.find(x => x.default)?.id
        ),
        this._zonesService.getAll().pipe(tap((res) => CreatorStateService.zones = res))
      ])),
      switchMap(() => of(CreatorStateService.seatsPlan))
    );
  }

  public loadCreatorSeatPriority(): Observable<ISeatsPlan> {
    return this._sectorsApiService.getSectorPriorityShape(CreatorStateService.sectorId).pipe(
      switchMap(() => this._sectorsApiService.getOneSector(CreatorStateService.sectorId))
    );
  }

  public loadCreatorConnections(): Observable<ISeatsPlan> {
    return this._sectorsApiService.getOneSector(CreatorStateService.sectorId);
  }

  public loadSector(id: number): Observable<ISeatsPlan> {
    CreatorStateService.sectorId = +id;

    let apiCalls$: Observable<unknown>[] = [
      this._sectorsApiService.getOneSector(
        CreatorStateService.sectorId,
        CreatorStateService.pickedConfiguration ? CreatorStateService.pickedConfiguration.id : null
      )
    ];

    const cachedSeatsPlan = CreatorCacheService.seatsPlansCacheMap.get(CreatorStateService.sectorId);

    if (cachedSeatsPlan) {
      apiCalls$ = [
        of(cachedSeatsPlan).pipe(
          tap((seatsPlan) => {
            CreatorStateService.seatsPlan = seatsPlan.seatsPlan;
            CreatorStateService.initSeatsPlanState = seatsPlan.initState;
          })
        )
      ];
    }

    if (CreatorStateService.currentStep === 'zones' && !CreatorStateService.movedToFirstSector) {
      apiCalls$.push(this._sectorsConfigurationsApiService.getAllSectorsConfigurations(CreatorStateService.sectorId));
    }

    if (CreatorStateService.currentStep === 'seat-priority') {
      const priorityCache = CreatorCacheService.priorityCacheMap.get(CreatorStateService.sectorId);

      if (priorityCache) {
        CreatorStateService.priorityShape = priorityCache.priorityShape;
        CreatorStateService.initPriorityShape = priorityCache.initPriorityShape;
        CreatorStateService.seatsGuidsWithManualPriority = priorityCache.seatsGuidsWithManualPriority;
      } else {
        apiCalls$.push(this._sectorsApiService.getSectorPriorityShape(CreatorStateService.sectorId));
      }
    }

    return forkJoin([...apiCalls$])
      .pipe(
        switchMap(() => this.getCreatorDataFromApi('shapes')),
        switchMap(() => of(CreatorStateService.seatsPlan)),
      );
  }

  public seatsPlanHasConnections(id?: number): boolean {
    const seatsPlan = id
      ? (CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan)
      : CreatorStateService.seatsPlan;

    return seatsPlan ? seatsPlan.seats.some(x => x.rightSeatDefinitionId || x.leftSeatDefinitionId) : false;
  }

  private getShapes(): Observable<boolean> {
    return this.getDataObservableWrapper((next: (value: boolean) => void, error: (err: string) => void) => {
      const cachedShapes = CreatorCacheService.shapesCache.get(CreatorStateService.sectorId);

      if (cachedShapes) {
        CreatorStateService.libraryShapes = cachedShapes.libraryShapes;
        CreatorStateService.sectorsShapes = cachedShapes.sectorsShapes;
        next(true);
      } else {
        forkJoin([
          this._sectorsApiService.getSectorShapes(CreatorStateService.sectorId),
          this._shapesApiService.getAll(CreatorStateService.seatsPlan.hasSectors)
        ]).pipe(tap(() => CreatorCacheService.cacheShapes()))
          .subscribe(() => next(true));
      }
    });
  }

  private getImages(): Observable<boolean> {
    return this.getDataObservableWrapper((next: (value: boolean) => void, error: (err: string) => void) => {
      if (!CreatorStateService.seatsPlan.hasSectors) {
        const cachedImages = CreatorStateService.libraryImageElements;

        if (!cachedImages?.length) {
          this._imageElementsApiService.getLibraryImageElements().subscribe(() => next(true));
        } else {
          next(true);
        }
      } else {
        next(true);
      }
    });
  }

  private getDataObservableWrapper(
    cb: (next: (value: boolean) => void, error: (err: string) => void) => void
  ): Observable<boolean> {
    const finished$ = new Subject<boolean>();

    setTimeout(() => {
      const next = (value: boolean) => {
        finished$.next(value);
        finished$.complete();
      };
      const error = (err: string) => {
        finished$.error(err);
        finished$.complete();
      };

      if (CreatorStateService.seatsPlan) {
        cb(next, error);
      } else {
        const err = 'no seats plan to make action on';
        console.error(err);
        error(err);
      }
    });

    return finished$.asObservable();
  }
}
